import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

const BADMINTON_STRENGTH_CONDITIONING_ROWS = [
  {
    exercise: 'Static Lunge with Cable Press',
    u9: 'Band press 2 x 6/side',
    u11: 'Light cable 2 x 6/side',
    u13: '2 x 8/side @ 2.5 kg',
    u15: '3 x 8/side @ 5 kg',
    u17: '3 x 8/side @ 7.5 kg',
  },
  {
    exercise: 'Single-leg Weight Transfer',
    u9: 'BW 2 x 8/side',
    u11: 'BW 2 x 10/side',
    u13: '2 x 10/side + 2 kg',
    u15: '3 x 10/side + 4 kg',
    u17: '3 x 12/side + 6 kg',
  },
  {
    exercise: '3 Skater Jumps into Tuck Jump',
    u9: '2 x 4 combos',
    u11: '2 x 5 combos',
    u13: '3 x 5 combos',
    u15: '3 x 6 combos',
    u17: '3 x 6 combos, stick landing',
  },
  {
    exercise: 'Badminton Lunges with Weight',
    u9: 'BW reach 2 x 6/side',
    u11: '2 kg 2 x 6/side',
    u13: '4 kg 2 x 8/side',
    u15: '6 kg 3 x 8/side',
    u17: '8 kg 3 x 8/side',
  },
  {
    exercise: 'Dumbbell Row Kneeling on Bench',
    u9: '1-2 kg 2 x 8/side',
    u11: '2-3 kg 2 x 8/side',
    u13: '4 kg 3 x 8/side',
    u15: '6 kg 3 x 8/side',
    u17: '8-10 kg 3 x 8/side',
  },
  {
    exercise: 'Impulse Physical Training',
    u9: 'Low pogo jumps 2 x 8',
    u11: 'Quick push-off hops 2 x 10',
    u13: 'Reactive hops 3 x 8',
    u15: 'Reactive bounds 3 x 8',
    u17: 'Reactive bounds 3 x 10',
  },
  {
    exercise: 'Dynamic Trunk Control',
    u9: 'Front/side hold 2 x 20s',
    u11: 'Dead bug + side hold 2 x 25s',
    u13: 'Pallof press 2 x 8/side',
    u15: 'Control circuit 3 x 8',
    u17: 'Control circuit 3 x 10',
  },
  {
    exercise: 'Barbell Lunges',
    u9: 'Dowel technique 2 x 6/side',
    u11: 'Light bar 2 x 6/side',
    u13: '10-15 kg 2 x 6/side',
    u15: '20-25 kg 3 x 6/side',
    u17: '25-35 kg 3 x 6/side',
  },
  {
    exercise: 'Medicine Ball Slams Ground',
    u9: '2 kg 2 x 6',
    u11: '2 kg 2 x 8',
    u13: '3 kg 3 x 6',
    u15: '4 kg 3 x 8',
    u17: '5 kg 3 x 8',
  },
  {
    exercise: 'Medicine Ball Slams Side Rotation (Wall)',
    u9: '1-2 kg 2 x 6/side',
    u11: '2 kg 2 x 8/side',
    u13: '3 kg 3 x 6/side',
    u15: '4 kg 3 x 8/side',
    u17: '5 kg 3 x 8/side',
  },
  {
    exercise: 'Step-up with Press',
    u9: '20 cm + 1 kg 2 x 6/side',
    u11: '25 cm + 2 kg 2 x 6/side',
    u13: '30 cm + 3 kg 3 x 6/side',
    u15: '35 cm + 4 kg 3 x 8/side',
    u17: '40 cm + 5 kg 3 x 8/side',
  },
  {
    exercise: 'Single-arm Dumbbell Row',
    u9: '1 kg 2 x 8/side',
    u11: '2 kg 2 x 8/side',
    u13: '4 kg 3 x 8/side',
    u15: '6 kg 3 x 10/side',
    u17: '8-10 kg 3 x 10/side',
  },
  {
    exercise: 'Bulgarian Split Squats',
    u9: 'BW 2 x 6/side',
    u11: 'BW 2 x 8/side',
    u13: '4 kg 3 x 6/side',
    u15: '6-8 kg 3 x 8/side',
    u17: '10-12 kg 3 x 8/side',
  },
  {
    exercise: 'Single-leg Kettlebell Weight Transfer',
    u9: '1 kg 2 x 6/side',
    u11: '2 kg 2 x 8/side',
    u13: '4 kg 2 x 8/side',
    u15: '6 kg 3 x 8/side',
    u17: '8 kg 3 x 10/side',
  },
] as const;

export function BadmintonStrengthConditioningTable() {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Age-banded practice targets for badminton strength and conditioning. Loads are indicative starting targets and should be adjusted by the coach based on movement quality and maturity.
      </Typography>
      <TableContainer sx={{ border: '1px solid #dbe8f6', borderRadius: 1, bgcolor: '#fff' }}>
        <Table size="small" sx={{ minWidth: 920 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f7fbff' }}>
              <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Exercise</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>U9</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>U11</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>U13</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>U15</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>U17</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {BADMINTON_STRENGTH_CONDITIONING_ROWS.map((row) => (
              <TableRow key={row.exercise}>
                <TableCell sx={{ fontWeight: 600 }}>{row.exercise}</TableCell>
                <TableCell>{row.u9}</TableCell>
                <TableCell>{row.u11}</TableCell>
                <TableCell>{row.u13}</TableCell>
                <TableCell>{row.u15}</TableCell>
                <TableCell>{row.u17}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
